import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Default Leaflet marker icons reference assets via webpack URLs which break
// without manual fixing. Use a CDN-hosted icon set so the pin shows up.
const PIN_ICON = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  shadowSize: [41, 41],
});

// Stellenbosch — sensible South African default for VinePass
const ZA_DEFAULT = { lat: -33.9167, lng: 18.8602, zoom: 10 };

const ClickToSet = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }),
  });
  return null;
};

const Recenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center?.lat != null && center?.lng != null) {
      map.flyTo([center.lat, center.lng], Math.max(map.getZoom(), 13), { duration: 0.6 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.lat, center?.lng]);
  return null;
};

const LocationPicker = ({ value, onChange, height = 280 }) => {
  const initial = value && value.lat != null && value.lng != null
    ? { lat: value.lat, lng: value.lng, zoom: 13 }
    : ZA_DEFAULT;

  return (
    <div data-testid="map-location-picker" className="rounded-xl overflow-hidden border border-stone-200 relative">
      <MapContainer
        center={[initial.lat, initial.lng]}
        zoom={initial.zoom}
        style={{ height: `${height}px`, width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <ClickToSet onPick={(p) => onChange?.(p)} />
        {value?.lat != null && value?.lng != null && (
          <>
            <Marker position={[value.lat, value.lng]} icon={PIN_ICON} />
            <Recenter center={value} />
          </>
        )}
      </MapContainer>
      <div className="absolute top-2 left-2 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-xs text-stone-600 shadow pointer-events-none">
        Tap on the map to drop a pin
      </div>
      {value?.lat != null && value?.lng != null && (
        <div data-testid="map-coords" className="px-3 py-2 bg-stone-50 border-t border-stone-200 text-xs text-stone-500 flex justify-between">
          <span>Pin: {value.lat.toFixed(4)}, {value.lng.toFixed(4)}</span>
          <button
            type="button"
            onClick={() => onChange?.({ lat: null, lng: null })}
            className="text-rose-700 hover:underline"
            data-testid="map-clear"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
